// web-admin/src/pages/setup/SecurityAccessPage.js
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
    Box, Typography, Paper, Grid, List, ListItem, ListItemText,
    ListItemButton, Checkbox, Button, Divider, Stack, Chip,
    TextField, Dialog, DialogTitle, DialogContent, DialogActions,
    IconButton, Tooltip, Fade, CircularProgress, Tabs, Tab
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    LockOutlined as LockIcon,
    GroupWork as ModuleIcon
} from '@mui/icons-material';
import AppHeader from '../../components/AppHeader';

const SecurityAccessPage = ({ setIsLoggedIn }) => {
    const [roles, setRoles] = useState([]);
    const [permissions, setPermissions] = useState([]);
    const [roleMappings, setRoleMappings] = useState([]);
    const [selectedRole, setSelectedRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [tabValue, setTabValue] = useState(0);

    // Dialog State
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [roleForm, setRoleForm] = useState({ role_code: '', role_name: '', description: '' });

    const tenant = localStorage.getItem("tenant") || "";

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const h = { "X-Tenant": tenant };
            const [rolesRes, permsRes, mappingsRes] = await Promise.all([
                axios.get(`${process.env.REACT_APP_API_URL1}/security/roles`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/security/permissions`, { withCredentials: true, headers: h }),
                axios.get(`${process.env.REACT_APP_API_URL1}/security/role-permissions`, { withCredentials: true, headers: h })
            ]);

            if (rolesRes.data.success) {
                setRoles(rolesRes.data.data);
                if (rolesRes.data.data.length > 0 && !selectedRole) {
                    setSelectedRole(rolesRes.data.data[0]);
                }
            }
            if (permsRes.data.success) setPermissions(permsRes.data.data);
            if (mappingsRes.data.success) setRoleMappings(mappingsRes.data.data);
        } catch (err) {
            console.error("Error fetching security data:", err);
        } finally {
            setLoading(false);
        }
    }, [tenant, selectedRole]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSavePermissions = async () => {
        if (!selectedRole) return;
        setSaving(true);
        try {
            const h = { "X-Tenant": tenant };
            const selectedPermIds = roleMappings
                .filter(m => m.role_id === selectedRole.id)
                .map(m => m.perm_id);

            await axios.post(`${process.env.REACT_APP_API_URL1}/security/role-permissions/${selectedRole.id}`, 
                { permissionIds: selectedPermIds }, 
                { withCredentials: true, headers: h }
            );
            alert("Permissions updated successfully!");
        } catch (err) {
            console.error("Error saving permissions:", err);
            alert("Failed to update permissions.");
        } finally {
            setSaving(false);
        }
    };

    const togglePermission = (permId) => {
        const isMapped = roleMappings.some(m => m.role_id === selectedRole.id && m.perm_id === permId);
        if (isMapped) {
            setRoleMappings(roleMappings.filter(m => !(m.role_id === selectedRole.id && m.perm_id === permId)));
        } else {
            setRoleMappings([...roleMappings, { role_id: selectedRole.id, perm_id: permId }]);
        }
    };

    const handleCreateRole = async () => {
        try {
            const h = { "X-Tenant": tenant };
            await axios.post(`${process.env.REACT_APP_API_URL1}/security/roles`, roleForm, { withCredentials: true, headers: h });
            setOpenRoleDialog(false);
            setRoleForm({ role_code: '', role_name: '', description: '' });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || "Error creating role");
        }
    };

    const handleDeleteRole = async (id) => {
        if (!window.confirm("Delete this role? This cannot be undone.")) return;
        try {
            const h = { "X-Tenant": tenant };
            await axios.delete(`${process.env.REACT_APP_API_URL1}/security/roles/${id}`, { withCredentials: true, headers: h });
            fetchData();
        } catch (err) {
            alert(err.response?.data?.message || "Error deleting role");
        }
    };

    const modules = [...new Set(permissions.map(p => p.module))];

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: '#f8fafc' }}>
                <CircularProgress color="primary" />
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader onLogout={() => { localStorage.clear(); setIsLoggedIn(false); window.location.href = '/login'; }} />
            
            <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
                <Fade in timeout={500}>
                    <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.02em' }}>
                                    Security & Access Management
                                </Typography>
                                <Typography variant="body2" sx={{ color: '#64748b' }}>
                                    Define roles and fine-tune permissions across the entire PeopleHub ecosystem.
                                </Typography>
                            </Box>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenRoleDialog(true)}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}
                            >
                                New Role
                            </Button>
                        </Stack>

                        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                            {/* Role Sidebar */}
                            <Box sx={{ width: 280, flexShrink: 0 }}>
                                <Paper sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                                    <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderBottom: '1px solid #e2e8f0' }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                            Available Roles
                                        </Typography>
                                    </Box>
                                    <List sx={{ py: 0 }}>
                                        {roles.map((role) => (
                                            <React.Fragment key={role.id}>
                                                <ListItem disablePadding>
                                                    <ListItemButton
                                                        selected={selectedRole?.id === role.id}
                                                        onClick={() => { setSelectedRole(role); setTabValue(0); }}
                                                        sx={{
                                                            py: 2,
                                                            '&.Mui-selected': {
                                                                bgcolor: (theme) => `${theme.palette.primary.main}33`,
                                                                borderRight: '4px solid #6366f1',
                                                                '&:hover': { bgcolor: (theme) => `${theme.palette.primary.main}33` }
                                                            }
                                                        }}
                                                    >
                                                        <ListItemText
                                                            primary={role.role_name}
                                                            secondary={role.role_code.toUpperCase()}
                                                            primaryTypographyProps={{ 
                                                                fontWeight: 700, 
                                                                color: selectedRole?.id === role.id ? 'primary.main' : '#1e293b',
                                                                noWrap: true,
                                                                sx: { overflow: 'hidden', textOverflow: 'ellipsis' }
                                                            }}
                                                            secondaryTypographyProps={{ fontSize: 10, fontWeight: 800 }}
                                                        />
                                                        {role.is_system_role === 1 ? (
                                                            <Tooltip title="System Protected">
                                                                <LockIcon sx={{ fontSize: 16, color: '#94a3b8' }} />
                                                            </Tooltip>
                                                        ) : (
                                                            <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        )}
                                                    </ListItemButton>
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        ))}
                                    </List>
                                </Paper>
                            </Box>

                            {/* Permission Matrix */}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Paper sx={{ borderRadius: 4, overflow: 'hidden', display: 'flex', flexDirection: 'column', minHeight: 500, boxShadow: '0 4px 20px rgba(0,0,0,0.04)' }}>
                                    <Box sx={{ p: 3, bgcolor: '#fff', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                                Permissions for {selectedRole?.role_name}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                {selectedRole?.description || 'No description provided.'}
                                            </Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                                            disabled={saving}
                                            onClick={handleSavePermissions}
                                            sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' }, borderRadius: 2, px: 4, fontWeight: 700, textTransform: 'none' }}
                                        >
                                            {saving ? 'Saving...' : 'Save Access Rules'}
                                        </Button>
                                    </Box>

                                    <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
                                        <Tabs
                                            value={tabValue}
                                            onChange={(e, v) => setTabValue(v)}
                                            variant="scrollable"
                                            scrollButtons="auto"
                                            sx={{
                                                '& .MuiTab-root': { py: 2, fontWeight: 700, textTransform: 'none', minWidth: 120 },
                                                '& .Mui-selected': { color: 'primary.main' },
                                                '& .MuiTabs-indicator': { bgcolor: 'primary.main' }
                                            }}
                                        >
                                            {modules.map((m, i) => <Tab key={m} label={m} icon={<ModuleIcon fontSize="small" />} iconPosition="start" />)}
                                        </Tabs>
                                    </Box>

                                    <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
                                        <Grid container spacing={2}>
                                            {permissions
                                                .filter(p => p.module === modules[tabValue])
                                                .map((perm) => {
                                                    const isSelected = roleMappings.some(m => m.role_id === selectedRole?.id && m.perm_id === perm.id);
                                                    return (
                                                        <Grid item xs={12} sm={6} key={perm.id}>
                                                            <Paper
                                                                variant="outlined"
                                                                sx={{
                                                                    p: 2, borderRadius: 3, border: isSelected ? '2px solid #6366f1' : '1px solid #e2e8f0',
                                                                    bgcolor: isSelected ? (theme) => `${theme.palette.primary.main}33` : 'transparent',
                                                                    transition: 'all 0.2s',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { bgcolor: (theme) => `${theme.palette.primary.main}0a` }
                                                                }}
                                                                onClick={() => togglePermission(perm.id)}
                                                            >
                                                                <Stack direction="row" spacing={2} alignItems="center">
                                                                    <Checkbox
                                                                        checked={isSelected}
                                                                        sx={{ color: '#cbd5e1', '&.Mui-checked': { color: 'primary.main' } }}
                                                                    />
                                                                    <Box>
                                                                        <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isSelected ? 'primary.main' : '#334155' }}>
                                                                            {perm.perm_name}
                                                                        </Typography>
                                                                        <Typography variant="caption" sx={{ color: '#64748b', display: 'block' }}>
                                                                            {perm.description || `Permission to access ${perm.perm_name.toLowerCase()} features.`}
                                                                        </Typography>
                                                                        <Chip label={perm.perm_code} size="small" sx={{ mt: 1, height: 16, fontSize: 9, fontWeight: 800, bgcolor: '#f1f5f9' }} />
                                                                    </Box>
                                                                </Stack>
                                                            </Paper>
                                                        </Grid>
                                                    );
                                                })}
                                        </Grid>
                                    </Box>
                                </Paper>
                            </Box>
                        </Box>
                    </Box>
                </Fade>
            </Box>

            {/* Create Role Dialog */}
            <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 450 } }}>
                <DialogTitle sx={{ fontWeight: 800, color: '#0f172a' }}>Create New Security Role</DialogTitle>
                <DialogContent>
                    <Stack spacing={3} sx={{ mt: 1 }}>
                        <TextField
                            fullWidth label="Role Code" placeholder="e.g. branch_auditor"
                            value={roleForm.role_code} onChange={(e) => setRoleForm({ ...roleForm, role_code: e.target.value })}
                            variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                        <TextField
                            fullWidth label="Role Name" placeholder="e.g. Branch Auditor"
                            value={roleForm.role_name} onChange={(e) => setRoleForm({ ...roleForm, role_name: e.target.value })}
                            variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                        <TextField
                            fullWidth label="Description" placeholder="What can this role do?"
                            multiline rows={3} value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                            variant="outlined" sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button onClick={() => setOpenRoleDialog(false)} color="inherit" sx={{ fontWeight: 700 }}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRole}
                        disabled={!roleForm.role_code || !roleForm.role_name}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' }, borderRadius: 2, px: 3, fontWeight: 700, textTransform: 'none' }}
                    >
                        Create Role
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SecurityAccessPage;
